import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, Plus, Search, Filter, ChevronLeft, ChevronRight, Edit3, Trash2,
  X, Calendar, DollarSign, TrendingUp, PieChart,
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import Skeleton from '../components/common/Skeleton';
import StatCard from '../components/common/StatCard';
import {
  fetchExpenses, createExpense, updateExpense, deleteExpense, fetchExpenseSummary,
  clearExpense, setPage,
} from '../redux/slices/expenseSlice';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const CATEGORIES = [
  'RENT', 'UTILITIES', 'SALARIES', 'TRANSPORTATION', 'PURCHASE',
  'MAINTENANCE', 'MARKETING', 'INSURANCE', 'TAXES', 'SUPPLIES',
  'FOOD', 'EQUIPMENT', 'MISC',
];

const PAYMENT_METHODS = ['CASH', 'ONLINE', 'CREDIT'];

const todayStr = () => new Date().toISOString().split('T')[0];

export default function Expenses() {
  const dispatch = useDispatch();
  const { expenses, loading, totalPages, currentPage, summary } = useSelector((state) => state.expenses);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    title: '', category: 'MISC', amount: '', description: '',
    expenseDate: todayStr(), paymentMethod: 'CASH', paidTo: '',
  });

  useEffect(() => {
    dispatch(fetchExpenses({ page: 1, limit: 50, startDate, endDate }));
    dispatch(fetchExpenseSummary({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const handlePageChange = (page) => {
    dispatch(setPage(page));
    dispatch(fetchExpenses({ page, limit: 50, startDate, endDate }));
  };

  const filteredExpenses = useMemo(() => {
    let result = expenses;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q) ||
          e.paidTo?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== 'All') {
      result = result.filter((e) => e.category === categoryFilter);
    }
    return result;
  }, [expenses, searchQuery, categoryFilter]);

  const resetForm = () => {
    setFormData({
      title: '', category: 'MISC', amount: '', description: '',
      expenseDate: todayStr(), paymentMethod: 'CASH', paidTo: '',
    });
  };

  const openCreateForm = () => {
    setEditingExpense(null);
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title || '',
      category: expense.category || 'MISC',
      amount: expense.amount || '',
      description: expense.description || '',
      expenseDate: expense.expenseDate ? expense.expenseDate.split('T')[0] : todayStr(),
      paymentMethod: expense.paymentMethod || 'CASH',
      paidTo: expense.paidTo || '',
    });
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) { toast.error('Expense title is required'); return; }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { toast.error('Valid amount is required'); return; }

    const payload = { ...formData, amount: parseFloat(formData.amount) };

    try {
      if (editingExpense) {
        await dispatch(updateExpense({
          id: editingExpense.expenseId || editingExpense._id,
          expenseData: payload,
        })).unwrap();
        toast.success('Expense updated');
      } else {
        await dispatch(createExpense(payload)).unwrap();
        toast.success('Expense recorded');
      }
      setShowForm(false);
      resetForm();
      setEditingExpense(null);
      dispatch(fetchExpenseSummary({ startDate, endDate }));
    } catch (err) {
      toast.error(err?.message || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteExpense(deleteTarget.expenseId || deleteTarget._id)).unwrap();
      setDeleteTarget(null);
      toast.success('Expense deleted');
      dispatch(fetchExpenseSummary({ startDate, endDate }));
    } catch (err) {
      toast.error(err?.message || 'Failed to delete expense');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `Rs.${num.toLocaleString()}`;
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Daily Expenses"
        description="Track and manage daily operational expenses"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" icon={Plus} onClick={openCreateForm}>
              Add Expense
            </Button>
          </div>
        }
      />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Wallet} label="Total Expenses" value={formatCurrency(summary.totalExpenses)} color="red" />
          <StatCard icon={PieChart} label="Categories" value={summary.categories?.length || 0} color="blue" />
          <StatCard icon={TrendingUp} label="Avg / Category" value={formatCurrency(
            summary.categories?.length > 0
              ? Math.round(summary.totalExpenses / summary.categories.length)
              : 0
          )} color="purple" />
        </div>
      )}

      {summary?.categories?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Category Breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {summary.categories.map((cat) => (
              <div key={cat._id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5 text-center">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{cat._id}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                  {formatCurrency(cat.total)}
                </p>
                <p className="text-[10px] text-gray-400">{cat.count} entries</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search expenses..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
          />
          <span className="text-gray-400">-</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
        >
          <option value="All">All Categories</option>
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="card" />
          ))}
        </div>
      ) : filteredExpenses.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No expenses found"
          description={searchQuery ? 'Try adjusting your search' : 'Record your first expense'}
          actionLabel="Add Expense"
          onAction={openCreateForm}
        />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Paid To</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                <AnimatePresence mode="popLayout">
                  {filteredExpenses.map((expense) => (
                    <motion.tr
                      key={expense._id || expense.expenseId}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {expense.expenseDate ? new Date(expense.expenseDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-white">{expense.title}</span>
                        {expense.description && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{expense.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          expense.paymentMethod === 'CASH'
                            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : expense.paymentMethod === 'ONLINE'
                            ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                            : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                        }`}>
                          {expense.paymentMethod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {expense.paidTo || '-'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditForm(expense)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(expense)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={18} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                page === currentPage
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      <Modal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingExpense(null); }}
        size="md"
        title={editingExpense ? 'Edit Expense' : 'Add Expense'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Title *</label>
              <input name="title" value={formData.title} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
              <textarea name="description" rows={2} value={formData.description} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 resize-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Category *</label>
              <select name="category" value={formData.category} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Amount *</label>
              <input name="amount" type="number" min="0" step="0.01" value={formData.amount} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Date</label>
              <input name="expenseDate" type="date" value={formData.expenseDate} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Payment Method</label>
              <select name="paymentMethod" value={formData.paymentMethod} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none">
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Paid To</label>
              <input name="paidTo" value={formData.paidTo} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowForm(false); setEditingExpense(null); }}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleSubmit} loading={loading}>
              {editingExpense ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Expense"
        message={`Are you sure you want to delete "${deleteTarget?.title}"?`}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
    </motion.div>
  );
}
